import { ALL_TIME_PRESET, presetToFilterRange } from '../ui/DateRangeSelect';
import type { ReportSortModelItem } from '../ReportPage/reportGridState';

/**
 * REG-828: the server-side state of the Submissions list and its workflow
 * submenus (Awaiting Documents, Unresponsive), shared by the grid
 * (SubmissionListGrid) and the sidebar's navigation prefetch, which must build
 * the exact same first request.
 */
export const SUBMISSIONS_REPORT_KEY = 'submissions';
/** Each user's columns on a submissions report grid, saved like any report page's. */
export const submissionListLayoutKey = (reportKey: string): string => `report_${reportKey}`;
export const SUBMISSION_LIST_LAYOUT_KEY = submissionListLayoutKey(SUBMISSIONS_REPORT_KEY);
/** Newest change first; public_submission_recent_idx serves it. */
export const SUBMISSION_LIST_DEFAULT_SORT: ReportSortModelItem[] = [{ colId: 'changed_at', sort: 'desc' }];

export const SUBMISSION_STATUS_OPTIONS = [
  ['all', 'All statuses'],
  ['funded', 'Funded'],
  ['approved', 'Approved'],
  ['contracts', 'Contracts Out'],
  ['declined', 'Declined'],
  ['awaiting', 'Awaiting Docs'],
  ['unresponsive', 'No Response'],
] as const;

export const SUBMISSION_STATUS_VALUES: Record<string, string[]> = {
  funded: ['Funded'],
  approved: ['Approved'],
  contracts: ['Contracts Out', 'Contracts In'],
  declined: ['Declined'],
  awaiting: ['Awaiting Documents', 'Awaiting Docs', 'Missing Documents', 'Missing documents', 'Waiting On'],
  unresponsive: ['Unresponsive', 'No Response'],
};

/**
 * The toolbar's status and date choices as fixed server filters. Their keys
 * are not column ids (each names its column in `colId`), so a column filter
 * the user sets on Status or Changed applies on top instead of replacing them.
 */
export function submissionListBaseFilterModel(status: string, datePreset: string): Record<string, any> {
  const model: Record<string, any> = {};
  if (status !== 'all') {
    model.__status_preset = { colId: 'status', filterType: 'set', values: SUBMISSION_STATUS_VALUES[status] ?? [] };
  }
  if (datePreset !== ALL_TIME_PRESET) {
    const { start, endExclusive } = presetToFilterRange(datePreset);
    const conditions = [
      ...(start ? [{ filterType: 'date', type: 'greaterThanOrEqual', filter: start }] : []),
      ...(endExclusive ? [{ filterType: 'date', type: 'lessThan', filter: endExclusive }] : []),
    ];
    if (conditions.length) model.__date_preset = { colId: 'changed_at', filterType: 'date', operator: 'AND', conditions };
  }
  return model;
}

function cleanSearch(value: string): string {
  return value.trim().replace(/[%_,().]/g, ' ').replace(/\s+/g, ' ').slice(0, 100);
}

/** "#123" or "123" finds that Sub ID; anything else is a full-text search. */
export function submissionSearchFilterModel(query: string): Record<string, any> | null {
  const normalized = cleanSearch(query);
  if (!normalized) return null;
  if (/^#?\d+$/.test(normalized)) {
    return { __search: { colId: 'submission_number', filterType: 'number', type: 'equals', filter: Number(normalized.replace('#', '')) } };
  }
  return { __search: { colId: 'full_text_search', filterType: 'text', type: 'plain_fts', filter: normalized } };
}

